/**
 * Which language the console renders in.
 *
 * Three rungs, in order, decided here and nowhere else so that no screen ever
 * grows its own opinion:
 *
 *   1. the signed-in user's stored preference   (no column for it yet, see below)
 *   2. what the browser asks for                 `navigator.languages`
 *   3. `en`
 *
 * Rung 1 has nothing to read yet. `users` and `users` carry no
 * `preferred_locale` column, so `preferred` is `null` at every call site today
 * and the resolver falls straight to the browser. The rung is written anyway
 * because the ORDER is the decision; which rungs currently return a value is a
 * fact about the schema, not about this module. When the column lands, one
 * caller starts passing a value and nothing here changes.
 *
 * # Matching, and why it is not `===`
 *
 * A browser asking for `en-GB` must be served the `en` catalogue rather than
 * falling to the default, and it must be served `en-GB` in preference to `en`
 * if we ever ship both. That is RFC 4647 "lookup": walk the requested tag from
 * most specific to least, take the first supported hit, then try the next
 * requested tag.
 *
 * # This is NOT the content locale chain
 *
 * `services/internal/localechain` resolves which locale a RECORD is served in,
 * over a project's configured locales and their fallback edges. This resolves
 * which language the CHROME is written in, over the catalogues we compiled into
 * the bundle. They share a spelling and nothing else: a project with `fr`
 * content on an English console is normal and correct.
 */

/** The default, and the only rung that can never fail. */
export const DEFAULT_LOCALE = 'en';

/**
 * Case-normalise a tag for comparison: `EN-gb` and `en-GB` are the same tag.
 *
 * `Intl.getCanonicalLocales` is the only BCP 47 grammar the browser ships, and
 * it throws rather than returning on a tag that does not parse. Anything
 * unparseable is dropped rather than guessed at. A malformed stored preference
 * should fall to the next rung, not take the console down.
 */
export function canonicalise(tag: string): string | null {
  const trimmed = tag.trim();
  if (!trimmed) return null;
  try {
    return Intl.getCanonicalLocales(trimmed)[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * `en-GB` -> `['en-GB', 'en']`. Most specific first, per RFC 4647 lookup.
 *
 * Singleton subtags (the `-u-` and `-x-` extensions) are truncated away with
 * everything after them rather than being walked one segment at a time, because
 * `en-u-ca` is not a language tag anyone ships a catalogue for.
 */
function progressivelyTruncate(tag: string): string[] {
  const parts = tag.split('-');
  const out: string[] = [];
  for (let i = parts.length; i > 0; i--) {
    const candidate = parts.slice(0, i);
    // A trailing single-character subtag is a dangling extension marker.
    if (candidate[candidate.length - 1].length === 1) continue;
    out.push(candidate.join('-'));
  }
  return out;
}

/**
 * Best supported match for one requested tag, or `null`.
 *
 * `supported` is matched case-insensitively; the value RETURNED is the one from
 * `supported`, so the caller always gets a tag it has a catalogue for rather
 * than the casing the browser happened to use.
 */
export function bestMatch(
  requested: string,
  supported: readonly string[],
): string | null {
  const canonical = canonicalise(requested);
  if (!canonical) return null;

  const byLower = new Map<string, string>();
  for (const s of supported) {
    const c = canonicalise(s);
    if (c) byLower.set(c.toLowerCase(), s);
  }

  for (const candidate of progressivelyTruncate(canonical)) {
    const hit = byLower.get(candidate.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

export interface ResolveInput {
  /**
   * The signed-in user's stored preference, or `null` when they have never
   * chosen. Null and `'en'` are different: null follows a changed default,
   * `'en'` does not. Always `null` until the column exists.
   */
  preferred?: string | null;
  /** Usually `navigator.languages`. Empty when there is no browser. */
  browser?: readonly string[];
  /** The catalogues actually compiled into this bundle. */
  supported: readonly string[];
}

/**
 * Walk the three rungs and return a locale that is guaranteed to be in
 * `supported`, falling back to {@link DEFAULT_LOCALE} even when it is not.
 * A console with no catalogue still has to render, and every string has an
 * in-code English default behind it.
 */
export function resolveLocale({
  preferred,
  browser = [],
  supported,
}: ResolveInput): string {
  if (preferred) {
    const hit = bestMatch(preferred, supported);
    if (hit) return hit;
  }
  for (const tag of browser) {
    const hit = bestMatch(tag, supported);
    if (hit) return hit;
  }
  return bestMatch(DEFAULT_LOCALE, supported) ?? DEFAULT_LOCALE;
}

/** `navigator.languages`, defensively, for the non-browser and old-browser cases. */
export function browserLocales(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}
